import { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useModalEsc } from "./modalStack";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
  showCloseButton?: boolean; // New prop to control close button visibility
  isFullscreen?: boolean; // Default to false for backwards compatibility
  // 바깥 오버레이(딤 배경)에 붙는 클래스. z-index 제어용. 기본은 z-99999.
  // 더 높은 z-index를 가진 다른 모달 위에 띄워야 할 때만 덮어쓴다
  // (예: 개통 신청서 수정 모달(z-100000) 안에서 열리는 중첩 모달).
  overlayClassName?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = true, // Default to true for backwards compatibility
  isFullscreen = false,
  overlayClassName = "z-99999",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Esc 는 중첩 모달 중 "최상단 하나만" 닫는다 — 전역 모달 스택이 판정(modalStack.ts).
  useModalEsc(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const contentClasses = isFullscreen
    ? "w-full h-full"
    : "relative w-full rounded-3xl bg-white  dark:bg-gray-900";

  return createPortal(
    // lnb-inset: 넓은 화면에서 왼쪽 메뉴를 비켜 그 오른쪽 끝부터 뜬다(티켓 #160, index.css).
    <div
      className={`fixed inset-0 lnb-inset flex items-center justify-center overflow-y-auto modal ${overlayClassName}`}
    >
      {!isFullscreen && (
        <div
          // 바깥 영역 클릭으로는 닫지 않음. 입력 중 실수 닫힘 방지. (닫기 X 버튼·Esc로만)
          // 딤도 같이 비켜야 메뉴가 흐려지지 않고 그대로 보인다.
          // (w-full/h-full 은 inset-0 과 중복이라 뺐다 — 남겨두면 비킨 만큼 오른쪽으로 넘친다.)
          className="fixed inset-0 lnb-inset bg-gray-400/50 backdrop-blur-[32px]"
        ></div>
      )}
      <div
        ref={modalRef}
        className={`${contentClasses}  ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute right-3 top-3 z-999 flex h-9.5 w-9.5 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white sm:right-6 sm:top-6 sm:h-11 sm:w-11"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.04289 16.5413C5.65237 16.9318 5.65237 17.565 6.04289 17.9555C6.43342 18.346 7.06658 18.346 7.45711 17.9555L11.9987 13.4139L16.5408 17.956C16.9313 18.3466 17.5645 18.3466 17.955 17.956C18.3455 17.5655 18.3455 16.9323 17.955 16.5418L13.4129 11.9997L17.955 7.4576C18.3455 7.06707 18.3455 6.43391 17.955 6.04338C17.5645 5.65286 16.9313 5.65286 16.5408 6.04338L11.9987 10.5855L7.45711 6.0439C7.06658 5.65338 6.43342 5.65338 6.04289 6.0439C5.65237 6.43442 5.65237 7.06759 6.04289 7.45811L10.5845 11.9997L6.04289 16.5413Z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
        <div>{children}</div>
      </div>
    </div>,
    document.body
  );
};

// 표준 모달 컴포넌트 — 신규 모달은 이쪽을 쓴다(Modal 은 저수준 껍데기).
// ModalShell: 헤더 + 자동 크기 본문 + 푸터 골격 / ModalFooter: 취소·저장 표준 바
// / FormModal: 입력+저장 폼 모달 고수준 래퍼.
export { ModalShell, type ModalSize, type ModalShellProps } from "./ModalShell";
export {
  ModalFooter,
  type ModalFooterProps,
  type ModalFooterButton,
} from "./ModalFooter";
export { FormModal, type FormModalProps } from "./FormModal";
